import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ThemeWrapper from "./ThemeWrapper";
import AuthRoute from "./components/Auth/AuthRoute";
import { AuthStatusProvider } from "./components/Auth/AuthStatusContext";
import Layout from "./components/Common/Layout";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { setGlobalToast } from "./services/ApiClient";
import { setGlobalAuthTokenGetter, debugAuthTokens } from "./utils/authUtils";
import { useAuth } from "react-oidc-context";
import env from "./config/env";
import { CircularProgress, Box } from "@mui/material";

// Lazy load route components for code splitting
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const AuthCallback = lazy(() => import("./components/Auth/AuthCallback"));
const Profile = lazy(() => import("./components/Auth/Profile"));
const OrganizationProfile = lazy(() => import("./components/Organization/OrganizationProfile"));
const OrganizationsList = lazy(() => import("./components/Organization/OrganizationsList"));
const CampaignDashboard = lazy(() => import("./components/CampaignDashboard/CampaignDashboard"));
const CampaignSettings = lazy(() => import("./components/Campaigns/CampaignSettings"));
const TrainingResourcesLibrary = lazy(() => import("./components/Training/TrainingResourcesLibrary"));
const ApplicationTemplates = lazy(() => import("./components/ApplicationTemplates/ApplicationTemplates"));
const EditTemplate = lazy(() => import("./components/ApplicationTemplates/EditTemplate"));
const CampaignsPage = lazy(() => import("./components/Campaigns/CampaignsPage"));
const ApplicantsPage = lazy(() => import("./components/Applicants/ApplicantsPage"));
const ApplicationPanel = lazy(() => import("./components/Applicants/ApplicationPanel"));
const PrivacyNotice = lazy(() => import("./components/Legal/PrivacyNotice"));
const Unauthorized = lazy(() => import("./components/Auth/Unauthorized"));
const SignOut = lazy(() => import("./components/Auth/SignOut"));
const SignInWrapper = lazy(() => import("./components/Auth/SignInWrapper"));
const SignUpWrapper = lazy(() => import("./components/Auth/SignUpWrapper"));
const ForgotPasswordWrapper = lazy(() => import("./components/Auth/ForgotPasswordWrapper"));
const MatchingScoresPage = lazy(() => import("./components/Matching/MatchingScoresPage"));
const ScoresPage = lazy(() => import("./components/Scores/ScoresPage"));
const MatchProposalsPage = lazy(() => import("./components/MatchProposals/MatchProposalsPage"));
const MatchDetailPage = lazy(() => import("./components/MatchDetail/MatchDetailPage"));
const MatchesPage = lazy(() => import("./components/Matches/MatchesPage"));

// Loading fallback component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);


// Component to set up global toast function
const ToastSetup: React.FC = () => {
  const { showToast } = useToast();

  useEffect(() => {
    setGlobalToast(showToast);
  }, [showToast]);

  return null;
};

// Component to set up global auth token getter
const AuthSetup: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    const getAccessToken = () => {
      // Debug auth tokens on first load
      if (process.env.NODE_ENV === 'development') {
        debugAuthTokens();
      }

      // Try to get access token from auth.user first
      if (auth.user?.access_token) {
        return auth.user.access_token;
      }


      return null;
    };

    setGlobalAuthTokenGetter(getAccessToken);
  }, [auth]);

  return null;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeWrapper>
        <ToastProvider>
          <ToastSetup />
          <AuthSetup />
          <Router basename={process.env.PUBLIC_URL || '/'} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AuthStatusProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/oauth2/idpresponse" element={<AuthCallback />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/signout" element={<SignOut />} />

                  {/* Cognito wrapper pages */}
                  <Route path="/signin" element={<SignInWrapper />} />
                  <Route path="/signup" element={<SignUpWrapper />} />
                  <Route path="/forgot-password" element={<ForgotPasswordWrapper />} />

                  {/* All protected routes wrapped in a single AuthRoute */}
                  <Route element={<AuthRoute />}>
                    {/* Dashboard routes */}
                    <Route path="/" element={<Layout><Dashboard /></Layout>} />
                    <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
                    <Route path="/profile" element={<Layout><Profile /></Layout>} />

                    {/* Organization routes */}
                    <Route path="/organizations/new" element={<Layout><OrganizationProfile /></Layout>} />
                    {/* Specific organization profile must come before generic list route */}
                    <Route path="/organizations/:id" element={<Layout><OrganizationProfile /></Layout>} />
                    {/* List all organizations */}
                    <Route path="/organizations" element={<Layout><OrganizationsList /></Layout>} />

                    {/* Training Resources */}
                    <Route path="/training-resources" element={<Layout><TrainingResourcesLibrary /></Layout>} />

                    {/* Application Templates */}
                    <Route path="/application-templates" element={<Layout><ApplicationTemplates /></Layout>} />
                    <Route path="/application-templates/:templateId/edit" element={<Layout><EditTemplate /></Layout>} />

                    {/* Campaigns */}
                    <Route path="/campaigns" element={<Layout><CampaignsPage /></Layout>} />

                    {/* Legal Pages */}
                    <Route path="/privacy" element={<Layout><PrivacyNotice /></Layout>} />

                    {/* Subscription routes */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/subscription"
                      element={<Layout fullScreen><CampaignDashboard /></Layout>} />

                    {/* Campaign routes - New Dashboard and Settings */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id"
                      element={<Layout fullScreen><CampaignDashboard /></Layout>} />
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/settings"
                      element={<Layout fullScreen><CampaignSettings /></Layout>} />
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/applicants"
                      element={<Layout fullScreen><ApplicantsPage /></Layout>} />
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/applicants/:applicant_id"
                      element={<Layout fullScreen><ApplicationPanel /></Layout>} />

                    {/* Legacy prep phase routes - redirect to settings */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep"
                      element={<Navigate to="../settings" replace />} />
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/*"
                      element={<Navigate to="../settings" replace />} />
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/launch"
                      element={<Navigate to="../settings" replace />} />

                    {/* Scores page */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/scores"
                      element={<Layout fullScreen><ScoresPage /></Layout>} />

                    {/* Match Proposals page */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/match-proposals"
                      element={<Layout fullScreen><MatchProposalsPage /></Layout>} />

                    {/* Matches page */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/matches"
                      element={<Layout fullScreen><MatchesPage /></Layout>} />

                    {/* Match Detail page */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/match-detail/:recommendation_id"
                      element={<Layout fullScreen><MatchDetailPage /></Layout>} />

                    {/* Matching phase routes */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/scores"
                      element={<Layout fullScreen><MatchingScoresPage /></Layout>} />

                    {/* Other matching routes - redirect to campaign dashboard */}
                    <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/*"
                      element={<Navigate to="../" replace />} />

                    {/* Catch-all route to redirect to dashboard */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Route>

                </Routes>
              </Suspense>
            </AuthStatusProvider>
          </Router>
        </ToastProvider>
      </ThemeWrapper>
    </ErrorBoundary>
  );
};

export default App;
